import React, { useEffect, useState } from "react";
import axios from "axios";
import { Bar } from "react-chartjs-2";
import BASE_URL from "components/BaseUrl/apiConfig";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";

// Register the necessary components for the chart
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const ColumnChartForMarketing = () => {
  const [chartData, setChartData] = useState({ labels: [], datasets: [] });
  const [totalMessageCount, setTotalMessageCount] = useState(0);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1); // Default to current month
  const [isAllSelected, setIsAllSelected] = useState(false);
  // Extract groupId from local storage
  const storedUserData = localStorage.getItem("userData");
  const groupId = storedUserData ? JSON.parse(storedUserData).groupId : "";

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await axios.get(
          `${BASE_URL}/api/Analytics/GetMonthlyMessageCounts?groupId=${groupId}&month=${selectedMonth}`
        );
        const data = response.data;

        if (!data || !data.UserMessageCounts) {
          throw new Error("Unexpected API response structure");
        }

        // Extract total message count
        setTotalMessageCount(data.TotalMessageCount);

        // Extract fullnames and message counts
        const fullnames = data.UserMessageCounts.map((user) => user.FullName);
        const emailCounts = data.UserMessageCounts.map(
          (user) => user.EmailCount
        );
        const facebookCounts = data.UserMessageCounts.map(
          (user) => user.FacebookCount
        );
        const linkedinCounts = data.UserMessageCounts.map(
          (user) => user.LinkedInCount
        );
        const othersCounts = data.UserMessageCounts.map(
          (user) => user.OthersCount
        );

        // Prepare datasets
        // Prepare datasets
        const datasets = [
          {
            label: "Total Messages",
            data: [
              data.TotalMessageCount,
              ...new Array(fullnames.length).fill(null),
            ], // Add placeholder for total messages
            backgroundColor: "#027148", // Yellow color
            borderColor: "#027148",
            barThickness: 20, // Adjust thickness for total messages
            order: 1, // Ensure this dataset is drawn on top
          },
          {
            label: "Email",
            data: [null, ...emailCounts], // Align user-specific data
            backgroundColor: "#0B0BFF",
            borderColor: "#0B0BFF",
            fill: false,
            barThickness: 20,
            order: 2,
          },
          {
            label: "Facebook",
            data: [null, ...facebookCounts], // Align user-specific data
            backgroundColor: "rgba(255, 77, 41, 0.8)",
            borderColor: "rgba(255, 77, 41, 0.8)",
            fill: false,
            barThickness: 20,
            order: 3,
          },
          {
            label: "LinkedIn",
            data: [null, ...linkedinCounts], // Align user-specific data
            backgroundColor: "rgba(9, 138, 150, 1)",
            borderColor: "rgba(9, 138, 150, 1)",
            fill: false,
            barThickness: 20,
            order: 4,
          },
          {
            label: "Others",
            data: [null, ...othersCounts], // Align user-specific data
            backgroundColor: "rgba(184, 25, 233, 1)",
            borderColor: "rgba(184, 25, 233, 1)",
            fill: false,
            barThickness: 20,
            order: 5,
          },
        ];

        // Update chart data
        setChartData({
          labels: ["Total Messages", ...fullnames], // Add "Total Messages" label at the beginning
          datasets: datasets,
        });
      } catch (error) {
        console.error("Error fetching data:", error.message);
      }
    };

    fetchData();
  }, [selectedMonth, groupId]);

  // Chart options
  const options = {
    responsive: true,
    plugins: {
      legend: {
        position: "top",
      },
      datalabels: {
        display: true,
        color: ["white"],
        formatter: (value) => value,
        font: {
          weight: "bold",
          size: 12,
          family: "Courier",
        },
        align: "start",
        offset: -10,
        padding: {
          top: 10,
          bottom: 10,
        },
        labels: {
          title: {
            font: {
              weight: "bold",
            },
          },
        },
      },
      tooltip: {
        callbacks: {
          title: function (tooltipItems) {
            // Show total message count as tooltip title
            return `Total Messages of this month: ${totalMessageCount}`;
          },
          label: function (context) {
            const label = context.dataset.label || "";
            const value = context.raw;
            return `${label}: ${value}`; // Ensure 0 is displayed
          },
        },
      },
    },

    scales: {
      x: {
        beginAtZero: true,
        title: {
          display: true,
          text: "Name",
        },
        grid: {
          borderColor: "rgba(33, 37, 41, 0.3)",
        },
        barPercentage: 0.5,
        categoryPercentage: 0.5,
      },
      y: {
        beginAtZero: true,
        title: {
          display: true,
          text: "Message Count",
        },
        ticks: {
          stepSize: 5,
        },
        grid: {
          borderColor: "rgba(33, 37, 41, 0.3)",
        },
      },
    },
  };

  const handleMonthChange = (e) => {
    setSelectedMonth(parseInt(e.target.value, 10));
    setIsAllSelected(false); // Reset "All" checkbox when a specific month is selected
  };

  const handleAllChange = (e) => {
    setIsAllSelected(e.target.checked);
    if (e.target.checked) {
      setSelectedMonth(0); // Set to 0 when "All" is selected
    } else {
      setSelectedMonth(new Date().getMonth() + 1); // Default to current month if unchecked
    }
  };

  return (
    <div className="relative flex flex-col min-w-0 break-words bg-white w-full mb-6 shadow-lg rounded">
      <div className="rounded mr-4 pl-4 mt-4 bg-transparent">
        <div className="flex flex-wrap items-center">
          <div className="relative w-full max-w-full flex-grow flex-1">
            <div className="flex justify-between">
              <h2 className="text-blueGray-700 text-xl font-semibold">
                Month Wise Messages by Users
              </h2>
              <div className="flex items-center gap-4 mr-4">
                {" "}
                {/* Added gap-4 for spacing */}
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="allMonths"
                    checked={isAllSelected}
                    onChange={handleAllChange}
                  />
                  <label htmlFor="allMonths" className="ml-2">
                    All
                  </label>{" "}
                  {/* Added ml-2 for label spacing */}
                </div>
                <select
                  className="bg-white text-black border border-gray-600 rounded px-6 py-1 outline-none focus:outline-none"
                  value={selectedMonth}
                  onChange={handleMonthChange}
                  disabled={isAllSelected} // Disable dropdown if "All" is selected
                  style={{ width: "140px" }}
                >
                  {[...Array(12).keys()].map((month) => (
                    <option key={month + 1} value={month + 1}>
                      {new Date(0, month).toLocaleString("default", {
                        month: "long",
                      })}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="p-4 flex-auto">
        {/* Chart */}
        <div className="relative h-96">
          <Bar data={chartData} options={options} />
        </div>
      </div>
    </div>
  );
};

export default ColumnChartForMarketing;
